import React from "react";
import { DesktopFrame, PhoneFrame, Chip } from "./frames";
import { BRAND, CATEGORIES, PROFESSIONS, POSITIONING, HERO_LINE, JOIN_CTA } from "../config/brand";
import { Search, Play, MapPin, ArrowRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/7882890/pexels-photo-7882890.jpeg?auto=compress&cs=tinysrgb&w=1100";
const POST_IMG = "https://images.pexels.com/photos/7882366/pexels-photo-7882366.jpeg?auto=compress&cs=tinysrgb&w=600";

const Nav = ({ small }) => (
  <div className="flex items-center justify-between border-b border-white/15 px-5 py-3">
    <div className="flex items-center gap-2 text-white">
      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-[#C4A46B] text-[11px] font-bold text-[#173A2C]">{BRAND.shortName}</div>
      <span className="font-serif text-lg leading-none">{BRAND.name}</span>
    </div>
    {!small && (
      <div className="hidden gap-5 text-[11px] text-white/80 md:flex">
        <span>Community</span><span>Jobs</span><span>Learning</span><span>For Employers</span>
      </div>
    )}
    <div className="rounded-full bg-[#C4A46B] px-3 py-1 text-[10px] font-semibold text-[#173A2C]">Sign in</div>
  </div>
);

const Hero = ({ compact }) => (
  <div className="relative">
    <img src={HERO} alt="Groomer working calmly with a horse" className="h-full w-full object-cover" style={{ height: compact ? 230 : 300 }} />
    <div className="absolute inset-0 bg-[#173A2C]/55" />
    <div className="grain absolute inset-0 opacity-20" />
    <div className="absolute inset-0 flex flex-col justify-center px-5">
      <div className="mb-2"><Chip tone="gold">{POSITIONING}</Chip></div>
      <h1 className={`font-serif font-light text-white ${compact ? "text-2xl" : "text-3xl md:text-4xl"} max-w-md leading-tight`}>
        {HERO_LINE}
      </h1>
      <p className="mt-2 max-w-sm text-[11px] text-white/85">Groomers, riders, jockeys, farriers, trainers & more. Verified profiles, verified employers, safe hiring.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button className="inline-flex items-center gap-1 rounded-full bg-[#C4A46B] px-4 py-2 text-[11px] font-semibold text-[#173A2C]">
          {JOIN_CTA} <ArrowRight className="h-3 w-3" />
        </button>
        <button className="inline-flex items-center gap-1 rounded-full border border-white/40 px-3 py-2 text-[11px] text-white">
          <Play className="h-3 w-3" /> Watch (muted)
        </button>
      </div>
    </div>
  </div>
);

const CatRow = () => (
  <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-[#E8E4DB] bg-white">
    {CATEGORIES.map((c, i) => (
      <span key={c} className={`rounded-full border px-2.5 py-1 text-[10px] ${i === 0 ? "border-[#1F4A38] bg-[#1F4A38] text-white" : "border-[#E8E4DB] text-[#5C5855]"}`}>{c}</span>
    ))}
  </div>
);

const JobCard = () => (
  <div className="rounded-md border border-[#E8E4DB] bg-white p-3">
    <div className="flex items-center justify-between">
      <Chip tone="green">Featured</Chip>
      <span className="text-[9px] text-[#5C5855]">2d ago</span>
    </div>
    <div className="mt-2 font-serif text-base text-[#173A2C]">Head Groom — Dressage Yard</div>
    <div className="mt-1 flex items-center gap-1 text-[10px] text-[#5C5855]"><MapPin className="h-3 w-3" /> Bavaria, Germany · Full-time</div>
    <div className="mt-2 flex gap-1"><Chip tone="slate">Clipping</Chip><Chip tone="slate">Plaiting</Chip><Chip tone="slate">EN/DE</Chip></div>
  </div>
);

const PostCard = () => (
  <div className="overflow-hidden rounded-md border border-[#E8E4DB] bg-white">
    <img src={POST_IMG} alt="community" className="h-24 w-full object-cover" />
    <div className="p-3">
      <Chip tone="gold">Best Practices</Chip>
      <div className="mt-1.5 text-[12px] font-medium text-[#2C2A29]">Cooling a horse safely after summer work</div>
      <div className="mt-1 text-[10px] text-[#5C5855]">42 likes · 12 comments</div>
    </div>
  </div>
);

export const HomepageDesktop = () => (
  <DesktopFrame testid="mock-home-desktop">
    <div className="h-full overflow-y-auto thin-scroll bg-[#FDFBF7]">
      <div className="bg-[#173A2C]"><Nav /><Hero /></div>
      <CatRow />
      <div className="grid grid-cols-3 gap-3 p-5">
        <div className="col-span-2 space-y-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C4A46B]">Professions in the community</div>
          <div className="flex flex-wrap gap-1.5">
            {PROFESSIONS.map((p) => (
              <span key={p.id} className="rounded-full border border-[#E8E4DB] px-2.5 py-1 text-[10px] text-[#2C2A29]">{p.label}</span>
            ))}
            <span className="rounded-full border border-dashed border-[#C4A46B] px-2.5 py-1 text-[10px] text-[#8a6f38]">+ more</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C4A46B]">Featured jobs</div>
          <div className="grid grid-cols-2 gap-3"><JobCard /><JobCard /></div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C4A46B]">From the community</div>
          <div className="grid grid-cols-2 gap-3"><PostCard /><PostCard /></div>
        </div>
        <div className="space-y-3">
          <div className="rounded-md border border-[#1F4A38] bg-[#1F4A38] p-4 text-white">
            <div className="font-serif text-lg">Are you an equestrian pro?</div>
            <p className="mt-1 text-[10px] text-white/80">Groomer, rider, jockey, farrier or trainer — build a verified profile and reach recruiters worldwide.</p>
            <div className="mt-2 rounded-full bg-[#C4A46B] px-3 py-1.5 text-center text-[10px] font-semibold text-[#173A2C]">Create candidate profile</div>
          </div>
          <div className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="font-serif text-base text-[#173A2C]">Hiring equestrian staff?</div>
            <p className="mt-1 text-[10px] text-[#5C5855]">Register your organization to search approved talent by profession.</p>
            <div className="mt-2 rounded-full border border-[#1F4A38] px-3 py-1.5 text-center text-[10px] font-semibold text-[#1F4A38]">Employer sign-up</div>
          </div>
        </div>
      </div>
    </div>
  </DesktopFrame>
);

export const HomepageMobile = () => (
  <PhoneFrame testid="mock-home-mobile">
    <div className="h-full overflow-y-auto thin-scroll bg-[#FDFBF7]">
      <div className="bg-[#173A2C]"><Nav small /><Hero compact /></div>
      <div className="flex gap-1.5 overflow-x-auto px-3 py-2 border-b border-[#E8E4DB] bg-white">
        {CATEGORIES.slice(0, 5).map((c, i) => (
          <span key={c} className={`whitespace-nowrap rounded-full border px-2 py-1 text-[9px] ${i === 0 ? "border-[#1F4A38] bg-[#1F4A38] text-white" : "border-[#E8E4DB] text-[#5C5855]"}`}>{c}</span>
        ))}
      </div>
      <div className="space-y-3 p-3">
        <JobCard />
        <PostCard />
      </div>
      <div className="sticky bottom-0 flex justify-around border-t border-[#E8E4DB] bg-white/95 py-2 text-[9px] text-[#5C5855] backdrop-blur">
        <span className="text-[#1F4A38] font-semibold">Feed</span><span>Jobs</span><span>Learning</span><span>Profile</span>
      </div>
    </div>
  </PhoneFrame>
);
